import { Mail, ShieldCheck } from "lucide-react";
import type { PreviewResponse } from "../../types/preview";
import type { ReleasedDocument } from "../../types/api";
import { formatBRL, formatISODateBR, formatTimestampBR } from "../../lib/format";
import { formatNotificadaQualification } from "../../lib/qualification";
import { VerifyTag } from "./VerifyTag";
import { hasNotificadaSummary } from "./NotificadaSummary";

type Variant = "screen" | "print";

export interface Fact {
  k: string;
  v: string;
}

const DEFAULT_TITLE = "Notificação Extrajudicial Tempestiva de Rescisão Contratual";

interface Props {
  facts: Fact[];
  preview: PreviewResponse;
  released: ReleasedDocument | null;
  variant: Variant;
}

const LABEL = "font-sans text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500";

function Parties({ preview, screen }: { preview: PreviewResponse; screen: boolean }) {
  const { notificante: n, notificada: d } = preview;
  return (
    <section className="mt-5 grid gap-5 border-b border-slate-300 pb-5 sm:grid-cols-2 print:grid-cols-2">
      <div data-testid={screen ? "document-notificante" : undefined}>
        <p className={LABEL}>Notificante</p>
        <p className="mt-1 font-semibold uppercase leading-snug">{n.nome}</p>
        <p className="text-sm text-slate-700">{n.documentoTipo ?? "CPF"} {n.documentoFormatado ?? n.cpfFormatado}</p>
        {n.conta && <p data-testid={screen ? "document-notificante-contato" : undefined} className="break-all text-sm text-slate-700">E-mail de contato: {n.conta}</p>}
      </div>
      <div data-testid={screen ? "document-notificada" : undefined}>
        <p className={`${LABEL} flex items-center gap-2`}>Notificada {screen && !d.verificado && <VerifyTag ok={false} testId="document-notificada-unverified" />}</p>
        <p data-testid={screen ? "document-notificada-qualificacao" : undefined} className="mt-1 font-semibold leading-snug">{formatNotificadaQualification(d)}</p>
        <p data-testid={screen ? "document-notificada-enderecamento" : undefined} className="text-sm italic text-slate-700">A/C {d.enderecamento}</p>
      </div>
    </section>
  );
}

function Channels({ preview }: { preview: PreviewResponse }) {
  if (!preview.emails.length) return null;
  return (
    <section data-testid="document-channels" className="mt-4 border-b border-slate-300 pb-4">
      <p className={LABEL}>Canais de destino</p>
      <ul className="mt-2 space-y-1.5">
        {preview.emails.map((e, i) => (
          <li key={e.email} data-testid={`document-channel-${i}`} className="flex flex-wrap items-center gap-2 font-sans text-xs text-slate-800">
            <Mail aria-hidden="true" className="h-3.5 w-3.5 text-slate-500" />
            <span className="break-all font-mono">{e.email}</span>
            <VerifyTag ok={e.verificado} testId={`document-channel-${i}-tag`} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function AuthStamp({ released, screen }: { released: ReleasedDocument; screen: boolean }) {
  return (
    <div data-testid={screen ? "document-auth-stamp" : undefined} className="mt-4 flex items-center gap-3 rounded-sm border-2 border-emerald-700 px-4 py-3 text-emerald-800">
      <ShieldCheck aria-hidden="true" className="h-7 w-7 shrink-0" />
      <div className="font-sans">
        <p className="text-xs font-bold uppercase tracking-[0.2em]">Liberado — Gepeto</p>
        <p className="text-xs">Em {formatTimestampBR(new Date(released.autenticadoEm))} · Protocolo {released.protocolo}</p>
      </div>
    </div>
  );
}

function Body({ preview, released, screen }: { preview: PreviewResponse; released: ReleasedDocument | null; screen: boolean }) {
  if (released) {
    return (
      <div data-testid={screen ? "document-body-released" : undefined} className="mt-6 space-y-4 text-justify text-sm leading-relaxed print:text-[11.5pt]">
        {released.paragrafos.map((p, i) => <p key={i} className="break-inside-avoid">{p}</p>)}
      </div>
    );
  }
  return (
    <div className="relative mt-6">
      <div aria-hidden="true" data-testid="document-body" className="pointer-events-none select-none space-y-4 text-justify text-sm leading-relaxed blur-[6px]">
        {preview.paragrafosOfuscados.map((p, i) => <p key={i}>{p}</p>)}
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <span data-testid="document-watermark" className="-rotate-[24deg] rounded-sm border-2 border-slate-500/40 px-4 py-2 text-center font-sans text-xs font-bold uppercase tracking-[0.16em] text-slate-600/70 sm:text-sm">
          Prévia - documento não liberado
        </span>
      </div>
    </div>
  );
}

function Highlight({ preview, screen }: { preview: PreviewResponse; screen: boolean }) {
  if (!preview.destaque) {
    return (
      <div data-testid={screen ? "document-certificate" : undefined} className="mt-5 rounded-sm border-2 border-emerald-600 bg-emerald-50 p-4 break-inside-avoid">
        <p className="font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800">Indicador de Tempestividade</p>
        <p className="mt-1.5 text-sm leading-relaxed sm:text-base">
          Notificação emitida tempestivamente em <strong data-testid={screen ? "document-days" : undefined}>D+{preview.diasDecorridos}</strong> dias dentro do prazo legal decadencial do Art. 49 do CDC.
        </p>
      </div>
    );
  }
  const d = preview.destaque;
  const items = [
    { id: "valor", k: "Valor retido", v: formatBRL(d.valorRetido) },
    { id: "data", k: "Data do bloqueio", v: formatISODateBR(d.dataBloqueio) },
    { id: "dias", k: "Tempo de retenção", v: `${d.diasRetencao} ${d.diasRetencao === 1 ? "dia" : "dias"} de retenção` },
  ];
  return (
    <div data-testid={screen ? "document-highlight" : undefined} className="mt-5 rounded-sm border-2 border-emerald-600 bg-emerald-50 p-4 break-inside-avoid">
      <p className="font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800">Quadro de Retenção</p>
      <dl className="mt-2 grid gap-3 sm:grid-cols-3 print:grid-cols-3">
        {items.map((i) => (
          <div key={i.id} data-testid={screen ? `document-highlight-${i.id}` : undefined}>
            <dt className="font-sans text-[10px] uppercase tracking-wider text-emerald-800/80">{i.k}</dt>
            <dd className="text-base font-bold">{i.v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function DocumentSheet({ facts, preview, released, variant }: Props) {
  const screen = variant === "screen";
  const summarized = hasNotificadaSummary(preview, released);
  const frame = screen
    ? "relative mx-auto w-full max-w-[794px] overflow-hidden rounded-sm bg-slate-50 shadow-2xl shadow-black/50 ring-1 ring-slate-300 md:aspect-[1/1.414]"
    : "bg-white";
  return (
    <article data-testid={screen ? "document-preview" : "print-sheet"} data-released={Boolean(released)} className={`${frame} font-serif text-slate-900`}>
      <div className={screen ? "p-6 sm:p-10 md:p-14" : ""}>
        <header className="border-b-2 border-slate-900 pb-5">
          <div className="flex flex-wrap items-center justify-between gap-2 font-sans">
            <span className="flex flex-col">
              <span className="flex items-center gap-1.5 text-base font-semibold tracking-tight"><span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-emerald-600" />Gepeto</span>
              <span className="text-[10px] text-slate-500">gepeto.life · CNPJ 68.800.228/0001-08</span>
            </span>
            <span data-testid={screen ? "document-protocol" : undefined} className="font-mono text-xs text-slate-700">Protocolo {preview.protocolo}</span>
          </div>
          <h4 data-testid={screen ? "document-title" : undefined} className="mt-5 text-center text-base font-bold uppercase leading-snug tracking-wide sm:text-lg">
            {preview.tituloDocumento ?? DEFAULT_TITLE}
          </h4>
          <p data-testid={screen ? "document-timestamp" : undefined} className="mt-2 text-center font-sans text-xs text-slate-600">Emitido em {formatTimestampBR(new Date(preview.emitidoEm))}</p>
        </header>
        <Highlight preview={preview} screen={screen} />
        {released && <AuthStamp released={released} screen={screen} />}
        <Parties preview={preview} screen={screen} />
        {screen && !summarized && <Channels preview={preview} />}
        <dl data-testid={screen ? "document-facts" : undefined} className="mt-4 grid grid-cols-1 gap-2 font-sans text-xs sm:grid-cols-3 print:grid-cols-3">
          {facts.map((f) => (
            <div key={f.k}><dt className="text-slate-500">{f.k}</dt><dd className="font-semibold text-slate-800">{f.v}</dd></div>
          ))}
        </dl>
        <Body preview={preview} released={released} screen={screen} />
      </div>
    </article>
  );
}
